import {
  KeenSliderHooks,
  KeenSliderInstance,
  useKeenSlider,
} from 'keen-slider/react';
import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from 'preact/compat';

import { useSettings } from 'components/SettingsProvider';

export function useSlider(
  slides: number,
  slidesPerView = 2,
  minSlideWidth = 0,
  maxSlideWidth = 0,
  spacing = 20,
  slideSelector: string,
) {
  const [loaded, setLoaded] = useState(false);
  const [hasNext, setHasNext] = useState(true);
  const [hasPrev, setHasPrev] = useState(false);
  const [perView, setPerView] = useState(slidesPerView);
  const [currentSlide, setCurrentSlide] = useState(0);
  const [controlsVisible, setControlsVisible] = useState(slides > perView);
  const [containerWidth, setContainerWidth] = useState(0);
  const [dotsVisible, setDotsVisible] = useState(slides > perView);
  const {
    utils: { isRtl },
  } = useSettings();

  const containerRef = useRef<HTMLInputElement>(null);

  const roundedPerView = perView < 1 ? 1 : Math.floor(perView);
  const dots = loaded ? Math.ceil(slides / roundedPerView) : 0;

  function handleSlideChange(
    instance: KeenSliderInstance<unknown, unknown, KeenSliderHooks>,
  ) {
    const currentSlide = Math.ceil(instance.track.details.abs / perView);

    setCurrentSlide(currentSlide);
    setHasNext(instance.track.details.rel < instance.track.details.maxIdx);
    setHasPrev(instance.track.details.rel > 0);
  }

  function handleOptionsChanged(
    instance: KeenSliderInstance<unknown, unknown, KeenSliderHooks>,
  ) {
    const perView = (
      instance.options.slides as { [key: string]: unknown; perView: number }
    ).perView;

    setPerView(perView);
    setControlsVisible(slides > perView);
    setCurrentSlide(0);
  }

  const [sliderRef, slider] = useKeenSlider({
    initial: 0,
    slides: {
      number: slides,
      spacing,
      perView,
    },
    rtl: isRtl,
    selector: `.${slideSelector}`,
    slideChanged: handleSlideChange,
    optionsChanged: handleOptionsChanged,
    created() {
      setLoaded(true);
    },
  });

  useEffect(() => {
    setPerView(perView);
  }, [slidesPerView, perView]);

  useEffect(() => {
    const instance = slider.current;

    instance && instance.update();

    return () => {
      instance && instance.destroy();
    };
  }, [slider, sliderRef]);

  useLayoutEffect(() => {
    const updateSize = () => {
      if (containerRef.current !== null) {
        const width = containerRef.current.offsetWidth;

        setContainerWidth(width ? width : 0);
      }
    };

    window.addEventListener('resize', updateSize);
    updateSize();

    return () => window.removeEventListener('resize', updateSize);
  }, [containerRef, setContainerWidth]);

  useEffect(() => {
    if (containerWidth !== 0 && minSlideWidth !== 0 && maxSlideWidth !== 0) {
      const defaultPerView = slidesPerView;

      let maxPerView =
        (containerWidth - minSlideWidth) / (minSlideWidth + spacing) + 1;
      let minPerView =
        (containerWidth - maxSlideWidth) / (maxSlideWidth + spacing) + 1;

      if (containerWidth > minSlideWidth * 2) {
        setDotsVisible(true);
        maxPerView = Math.floor(maxPerView);
        minPerView = Math.ceil(minPerView);
      } else {
        setDotsVisible(false);
        maxPerView = +maxPerView.toFixed(2);
        minPerView = +minPerView.toFixed(2);
      }

      const isDefaultSuitable =
        maxPerView >= defaultPerView && minPerView <= defaultPerView;

      const ignore =
        perView === defaultPerView &&
        defaultPerView >= minPerView &&
        defaultPerView <= maxPerView;

      const setDefault = perView !== defaultPerView && isDefaultSuitable;

      const setMaxPerView = !isDefaultSuitable && minPerView < 1;

      const setMinDiviation = !isDefaultSuitable;

      if (!ignore) {
        if (setDefault) {
          setPerView(defaultPerView);
        } else if (setMaxPerView) {
          setPerView(maxPerView);
        } else if (setMinDiviation) {
          const d1 = Math.abs(defaultPerView - maxPerView);
          const d2 = Math.abs(defaultPerView - minPerView);

          if (d1 > d2) {
            if (minPerView !== perView) setPerView(minPerView);
          } else if (maxPerView !== perView) setPerView(maxPerView);
        }
      }
    } else if (containerWidth !== 0 && minSlideWidth !== 0) {
      const defaultPerView = slidesPerView;

      let maxPerView =
        (containerWidth - minSlideWidth) / (minSlideWidth + spacing) + 1;

      if (containerWidth > minSlideWidth * 2) {
        setDotsVisible(true);
        maxPerView = Math.floor(maxPerView);
      } else {
        setDotsVisible(false);
        maxPerView = +maxPerView.toFixed(2);
      }

      const isDefaultSuitable = maxPerView >= defaultPerView;

      const ignore = perView === defaultPerView && defaultPerView <= maxPerView; // если 1 в пределах допустимого (все карточки влезают)

      const setDefault = perView !== defaultPerView && isDefaultSuitable;

      const setMaxPerView = !isDefaultSuitable;

      if (!ignore) {
        if (setDefault) {
          setPerView(defaultPerView);
        } else if (setMaxPerView) {
          setPerView(maxPerView);
        }
      }
    }
  }, [
    spacing,
    slidesPerView,
    containerWidth,
    perView,
    minSlideWidth,
    maxSlideWidth,
    setPerView,
    setDotsVisible,
  ]);

  const next = useCallback(() => {
    const maxIdx = slider.current?.track.details.maxIdx ?? slides;

    slider.current?.moveToIdx(
      Math.min(slider.current?.track.details.rel + perView, maxIdx),
    );
  }, [slider, perView, slides]);

  const prev = useCallback(
    () =>
      slider.current?.moveToIdx(
        Math.max(slider.current?.track.details.rel - perView, 0),
      ),
    [slider, perView],
  );

  const goToSlide = useCallback(
    (index: number) => {
      const maxIdx = slider.current?.track.details.maxIdx ?? slides;

      slider.current?.moveToIdx(Math.min(index * perView, maxIdx));
    },
    [slider, perView, slides],
  );

  return {
    currentSlide,
    controlsVisible,
    goToSlide,
    dots,
    sliderRef,
    next,
    prev,
    hasNext,
    hasPrev,
    setPerView,
    perView,
    dotsVisible,
    containerRef,
    loaded,
  };
}
